import {
  CodeBracketSquareIcon,
  LinkIcon,
  ListBulletIcon,
  NoSymbolIcon
} from '@heroicons/react/24/outline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import { type Editor, EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useCallback } from 'react'
import './RichTextEditor.css'

// --- MenuBar Component ---
interface MenuBarProps {
  editor: Editor | null
}

const iconClass = 'h-4 w-4'

const MenuBar = ({ editor }: MenuBarProps) => {
  const setLink = useCallback(() => {
    if (!editor) return
    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)

    // cancelled
    if (url === null) {
      return
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    // update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }, [editor])

  if (!editor) {
    return null
  }

  const buttons = [
    {
      action: () => editor.chain().focus().toggleBold().run(),
      icon: 'B',
      active: editor.isActive('bold'),
      title: 'Bold',
      style: { fontWeight: 'bold' }
    },
    {
      action: () => editor.chain().focus().toggleItalic().run(),
      icon: <span style={{ fontStyle: 'italic' }}>I</span>,
      active: editor.isActive('italic'),
      title: 'Italic'
    },
    {
      action: () => editor.chain().focus().toggleUnderline().run(),
      icon: <span style={{ textDecoration: 'underline' }}>U</span>,
      active: editor.isActive('underline'),
      title: 'Underline'
    },
    {
      action: () => editor.chain().focus().toggleStrike().run(),
      icon: 'S',
      active: editor.isActive('strike'),
      title: 'Strike',
      style: { textDecoration: 'line-through' }
    },
    {
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      icon: 'H2',
      active: editor.isActive('heading', { level: 2 }),
      title: 'Heading 2'
    },
    {
      action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      icon: 'H3',
      active: editor.isActive('heading', { level: 3 }),
      title: 'Heading 3'
    },
    {
      action: () => editor.chain().focus().toggleBulletList().run(),
      icon: <ListBulletIcon className={iconClass} />,
      active: editor.isActive('bulletList'),
      title: 'Bullet List'
    },
    {
      action: () => editor.chain().focus().toggleOrderedList().run(),
      icon: '1.',
      active: editor.isActive('orderedList'),
      title: 'Ordered List'
    },
    {
      action: () => editor.chain().focus().toggleCodeBlock().run(),
      icon: <CodeBracketSquareIcon className={iconClass} />,
      active: editor.isActive('codeBlock'),
      title: 'Code Block'
    },
    {
      action: setLink,
      icon: <LinkIcon className={iconClass} />,
      active: editor.isActive('link'),
      title: 'Link'
    },
    {
      action: () => editor.chain().focus().unsetLink().run(),
      icon: <NoSymbolIcon className={iconClass} />,
      disabled: !editor.isActive('link'),
      title: 'Unlink'
    }
  ]

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border border-b-0 border-[var(--ds-border)] rounded-t-[3px] bg-[var(--ds-surface-raised)]">
      {buttons.map(btn => (
        <button
          key={btn.title}
          type="button"
          onClick={btn.action}
          disabled={btn.disabled}
          className={`flex items-center justify-center p-1 rounded-[3px] hover:bg-[var(--ds-background-neutral-hover)] disabled:opacity-50 disabled:cursor-not-allowed ${
            btn.active
              ? 'bg-[var(--ds-background-neutral-subtle-pressed)] text-[var(--ds-text-selected)]'
              : 'text-[var(--ds-text-subtle)]'
          }`}
          title={btn.title}
          style={btn.style}
        >
          {btn.icon}
        </button>
      ))}
    </div>
  )
}

// --- RichTextEditor Component ---
interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  onBlur?: () => void
  minHeight?: string | number
  placeholder?: string
  dataTestId?: string
  ariaLabel?: string
}

export const RichTextEditor = ({
  value,
  onChange,
  onBlur,
  minHeight = 150,
  placeholder,
  dataTestId,
  ariaLabel
}: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3]
        },
        codeBlock: true
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Enter text...'
      })
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    onBlur: () => {
      onBlur?.()
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none p-3 border border-[var(--ds-border)] rounded-b-[3px] bg-[var(--ds-surface)] focus:outline-none focus:border-[var(--ds-border-focused)] text-[var(--ds-text)]',
        style: `min-height: ${typeof minHeight === 'number' ? `${minHeight}px` : minHeight};`,
        'data-testid': dataTestId ? `${dataTestId}-editor` : undefined,
        'aria-label': ariaLabel
      }
    }
  })

  return (
    <div data-testid={dataTestId}>
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  )
}
